import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";

// Public Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import TeamPage from "@/pages/TeamPage";
import ContactPage from "@/pages/ContactPage";
import CCFSPage from "@/pages/CCFSPage";
import CCFSv2Page from "@/pages/CCFSv2Page";
import RPaceLoginPage from "@/pages/RPaceLoginPage";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientForm from "@/pages/admin/AdminClientForm";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import MicroCFOWorkspace from "@/pages/admin/clients/MicroCFOWorkspace";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminCCFSLeads from "@/pages/admin/AdminCCFSLeads";
import AdminPartnerRequests from "@/pages/admin/AdminPartnerRequests";
import AdminCases from "@/pages/admin/AdminCases";
import AdminCaseDetail from "@/pages/admin/AdminCaseDetail";
import AdminCaseNew from "@/pages/admin/AdminCaseNew";
import AdminCalculatorPage from "@/pages/admin/AdminCalculatorPage";

// Partner Pages
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerCaseDetail from "@/pages/partner/PartnerCaseDetail";
import PartnerCalculatorPage from "@/pages/partner/PartnerCalculatorPage";
import PartnerLeadsPage from "@/pages/partner/PartnerLeadsPage";
import PartnerEngagementsPage from "@/pages/partner/PartnerEngagementsPage";
import PartnerCreditsPage from "@/pages/partner/PartnerCreditsPage";
import PartnerPitchKitPage from "@/pages/partner/PartnerPitchKitPage";

// Admin Extra Pages
import AdminTipsPage from "@/pages/admin/AdminTipsPage";
import AdminFormLibrary from "@/pages/admin/AdminFormLibrary";
import AdminSchemeSettings from "@/pages/admin/AdminSchemeSettings";

// Layout Components
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import PartnerLayout from "@/components/layout/PartnerLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

function PartnerGuard({ children }) {
  const token = localStorage.getItem('rpace_token');
  const partner = localStorage.getItem('rpace_partner');
  if (!token || !partner) return <Navigate to="/r-pace-partner-login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/ccfs" element={<CCFSPage />} />
            <Route path="/ccfs-2026-company-status-check" element={<CCFSv2Page />} />
          </Route>

          {/* R-PACE Partner Routes */}
          <Route path="/r-pace-partner-login" element={<RPaceLoginPage />} />
          <Route element={<PartnerGuard><PartnerLayout /></PartnerGuard>}>
            <Route path="/r-pace/dashboard" element={<PartnerDashboard />} />
            <Route path="/r-pace/pipeline" element={<PartnerLeadsPage />} />
            <Route path="/r-pace/leads" element={<PartnerLeadsPage />} />
            <Route path="/r-pace/calculator" element={<PartnerCalculatorPage />} />
            <Route path="/r-pace/engagements" element={<PartnerEngagementsPage />} />
            <Route path="/r-pace/cases/:id" element={<PartnerCaseDetail />} />
            <Route path="/r-pace/credits" element={<PartnerCreditsPage />} />
            <Route path="/r-pace/pitch-kit" element={<PartnerPitchKitPage />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/clients" element={<AdminClients />} />
            <Route path="/admin/clients/new" element={<AdminClientForm />} />
            <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
            <Route path="/admin/clients/:id/workspace" element={<MicroCFOWorkspace />} />
            <Route path="/admin/clients/:id/edit" element={<AdminClientForm />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/ccfs-leads" element={<AdminCCFSLeads />} />
            <Route path="/admin/partner-requests" element={<AdminPartnerRequests />} />
            <Route path="/admin/cases" element={<AdminCases />} />
            <Route path="/admin/cases/new" element={<AdminCaseNew />} />
            <Route path="/admin/cases/:id" element={<AdminCaseDetail />} />
            <Route path="/admin/calculator" element={<AdminCalculatorPage />} />
            <Route path="/admin/tips" element={<AdminTipsPage />} />
            <Route path="/admin/form-library" element={<AdminFormLibrary />} />
            <Route path="/admin/scheme-settings" element={<AdminSchemeSettings />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-right" richColors closeButton duration={6000} />
    </div>
  );
}

export default App;
